import { Button, Card, Form, Select, Modal } from '@bancobarigui/barihaus';
import { Input as AntInput, Form as AntForm } from 'antd';
import { PlusOutlined, DragOutlined, RobotOutlined, LoadingOutlined, ExclamationCircleOutlined, CalculatorOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';

import {
  AmostrasData,
  DadosDepreciacao,
  AvaliacaoData,
  DadosAreaConstruida,
  DadosValorTerreno,
  DadosComparativoDireto,
  IdentificacaoData,
  WizardMode,
  TipoImovelAmostra,
} from '@/features/laudo/types/laudo-wizard.types';
import type { AmostraPadrao } from '@/features/laudo/mock/amostrasPadronizadas';
import { InspectionRequest } from '@/services/types/inspection.types';
import { PropertyData } from '@/services/scraper.service';
import { inspectionService } from '@/services/inspection.service';
import { AmostrasScraping } from './AmostrasScraping';
import { CalculoDepreciacao } from './CalculoDepreciacao';
import { TabelaAreaConstruida } from './TabelaAreaConstruida';
import { TabelaValorTerreno } from './TabelaValorTerreno';
import { TabelaComparativoDireto } from './TabelaComparativoDireto';
import { CardHomogeneizacao } from './CardHomogeneizacao';
import { CardFormacaoValor } from './CardFormacaoValor';
import { ClassificacaoSection } from './CalculosAvaliatoriasPanel/components/ClassificacaoSection';
import { TerrenoSection } from './CalculosAvaliatoriasPanel/components/TerrenoSection';
import { AreaConstruidaSection } from './CalculosAvaliatoriasPanel/components/AreaConstruidaSection';
import { AreaNaoAverbadaSection } from './CalculosAvaliatoriasPanel/components/AreaNaoAverbadaSection';
import { ResumoFinalSection } from './CalculosAvaliatoriasPanel/components/ResumoFinalSection';
import {
  useValorParcialTerreno,
  useValorParcialConstruida,
  useValorNaoAverbada,
  useValorTotal,
} from './CalculosAvaliatoriasPanel/hooks';
import { calcularLimitesHomogeneizacao, valorDentroDoIntervalo } from './CardHomogeneizacao/utils';
import { getValorUnitarioConstrucaoAvaliando } from '@/features/laudo/utils/calculoConstrucaoAvaliando';
import { AmostraModal } from './AmostraCard';
import { AmostrasList } from './AmostrasList';
import type { AlertaItem } from './AlertaGlobal';
import { dadosAreaConstruidaMock } from '@/features/laudo/mock/dadosAreaConstruidaMock';
import {
  dadosValorTerrenoMock,
  switchesTerrenoMock,
} from '@/features/laudo/mock/dadosValorTerrenoMock';
import { dadosDepreciacaoMock } from '@/features/laudo/mock/dadosDepreciacaoMock';
import { ReviewFieldWrapper } from '../../ReviewFieldWrapper';
import { useReviewMode } from '../../index';
import { useAiAnalysis } from './useAiAnalysis';
import { RelatorioAnaliseIA } from './RelatorioAnaliseIA';

// As amostras (exceto o Avaliando) devem manter identificação sequencial #1..#N
// na ordem escolhida. Exclusões e rascunhos antigos deixam "buracos" (ex.: #4, #5
// após excluir #1..#3), fazendo a numeração da tela divergir da numeração
// renumerada do PDF. Renumera e devolve o mapeamento de IDs antigos → novos.
function renumerarAmostrasSequencialmente(amostras: AmostraPadrao[]): {
  amostras: AmostraPadrao[];
  idMapping: Record<string, string>;
} {
  const idMapping: Record<string, string> = {};
  let contador = 0;
  const renumeradas = amostras.map((amostra) => {
    if (amostra.identificacao === 'Avaliando') return amostra;
    const novoId = `#${++contador}`;
    if (amostra.identificacao === novoId) return amostra;
    idMapping[amostra.identificacao] = novoId;
    return { ...amostra, identificacao: novoId };
  });
  return { amostras: renumeradas, idMapping };
}

// Propaga a renumeração de IDs para as tabelas de cálculo, mantendo fatores e
// valores editados associados às amostras corretas.
function renomearIdsNasTabelas(
  idMapping: Record<string, string>,
  tabelas: {
    dadosDepreciacao: DadosDepreciacao[];
    dadosAreaConstruida: DadosAreaConstruida[];
    dadosValorTerreno: DadosValorTerreno[];
    dadosComparativoDireto: DadosComparativoDireto[];
  }
) {
  const renameId = (id: string) => idMapping[id] || id;
  return {
    dadosDepreciacao: tabelas.dadosDepreciacao.map((d) => ({ ...d, identificacao: renameId(d.identificacao) })),
    dadosAreaConstruida: tabelas.dadosAreaConstruida.map((d) => ({ ...d, identificacao: renameId(d.identificacao) })),
    dadosValorTerreno: tabelas.dadosValorTerreno.map((d) => ({ ...d, amostra: renameId(d.amostra) })),
    dadosComparativoDireto: tabelas.dadosComparativoDireto.map((d) => ({ ...d, amostra: renameId(d.amostra) })),
  };
}

interface AmostrasStepProps {
  data: AmostrasData;
  onUpdate: (data: Partial<AmostrasData>) => void;
  inspection: InspectionRequest;
  avaliacaoData: AvaliacaoData; // Dados do step de Avaliação para sincronização
  identificacaoData?: IdentificacaoData; // Dados do step de Identificação
  fotoFachada?: string; // Foto de fachada do step de Vistoria
  mode?: WizardMode; // Modo do wizard (create, edit, review, calculations)
  onSkipCalculations?: () => void; // Callback para pular cálculos (enviar laudo sem amostras)
}

export function AmostrasStep({ data, onUpdate, inspection, avaliacaoData, identificacaoData, fotoFachada, mode, onSkipCalculations }: AmostrasStepProps) {
  const isReviewMode = mode === 'review';
  const isCorrectionMode = mode === 'correction';
  const { reviewContext } = useReviewMode();

  // Hook para buscar análise de IA do inspection item
  const inspectionItemId = inspection?.items?.[0]?.id || '';
  const { aiAnalysisResult, isProcessing: aiIsProcessing, isCompleted: aiIsCompleted, isFailed: aiIsFailed } = useAiAnalysis({
    inspectionItemId,
  });

  // Flag para controlar se as amostras IA já foram importadas automaticamente
  const [aiSamplesAutoImported, setAiSamplesAutoImported] = useState(false);

  // Verifica se é devolução externa (status 21) - sem correções pendentes específicas
  // Quando é devolução externa, todos os campos e botões devem ser habilitados
  const isExternalReturn = isCorrectionMode && (!reviewContext?.pendingCorrections || reviewContext.pendingCorrections.length === 0);

  // Em modo correção, qualquer correção pendente na aba de amostras habilita TODA a aba
  // Isso resolve o problema de campos não selecionáveis individualmente (QA 4.21)
  const hasAnyAmostrasCorrection = isExternalReturn || (isCorrectionMode && reviewContext?.pendingCorrections?.some(
    c => c.fieldPath.startsWith('amostras.') || c.fieldPath === 'avaliacao.dadosImovel.tipoImovel'
  ));

  const hasSamplesCorrection = hasAnyAmostrasCorrection;
  const hasMetodologiaCorrection = hasAnyAmostrasCorrection;
  const hasComparativoDiretoCorrection = hasAnyAmostrasCorrection;
  const hasDepreciacaoCorrection = hasAnyAmostrasCorrection;
  const hasAreaConstruidaCorrection = hasAnyAmostrasCorrection;
  const hasValorTerrenoCorrection = hasAnyAmostrasCorrection;
  const hasAreaNaoAverbadaCorrection = hasAnyAmostrasCorrection;

  // Cria a linha base do "Avaliando" a partir dos steps anteriores.
  // Usado tanto na inicialização quanto como fallback quando o draft salvo
  // não contém uma linha de Avaliando (ex.: importação só de amostras pela IA,
  // edição manual de draft, bugs históricos), evitando que o método evolutivo
  // fique sem o imóvel avaliando e zere o valor final.
  const criarAvaliandoInicial = (): AmostraPadrao => ({
    identificacao: 'Avaliando',
    tipoImovel: (avaliacaoData?.dadosImovel?.tipoImovel as TipoImovelAmostra) || 'Casa',
    areaPrivativa: avaliacaoData?.dadosImovel?.areaConstruida || identificacaoData?.areaPrivativa || 0,
    areaTerreno: avaliacaoData?.dadosTerreno?.areaTerreno || 0,
    valorTotal: 0,
    idadeAparente: avaliacaoData?.dadosImovel?.idadeAparente || 0,
    estadoConservacao: (avaliacaoData?.dadosImovel?.estadoConservacao as 'Novo' | 'Bom' | 'Regular' | 'Reparos Simples' | 'Reparos Importantes' | 'Sem Valor') || 'Regular',
    cubBase: 2500,
    ajusteAcabamento: 1.0,
    padrao: 'Médio',
    topografia: 'Terreno Plano',
    fonte: 'avaliando',
  });

  // Estado local para as amostras (fonte única de verdade)
  // Inicializa a partir dos dados salvos OU cria "Avaliando" inicial
  const [amostrasLocais, setAmostrasLocais] = useState<AmostraPadrao[]>(() => {
    // Se já existem dados salvos nas tabelas, reconstrói as amostras a partir deles
    if (data.dadosDepreciacao && data.dadosDepreciacao.length > 0) {
      // Reconstrói as amostras a partir dos dados de depreciação (que tem todas as amostras)
      const amostrasReconstruidas = data.dadosDepreciacao.map((dep) => {
        // Busca dados complementares nas outras tabelas
        const valorTerreno = data.dadosValorTerreno?.find(v => v.amostra === dep.identificacao);
        const areaConstruida = data.dadosAreaConstruida?.find(a => a.identificacao === dep.identificacao);
        const comparativoDireto = data.dadosComparativoDireto?.find(c => c.amostra === dep.identificacao);

        // IMPORTANTE: Busca dados originais da amostra cadastrada (contém quartos, banheiros, vagas, etc.)
        // Esses dados são salvos em data.amostras pelo useEffect que sincroniza amostrasLocais
        const amostraCadastrada = data.amostras?.find((a: any) => a.id === dep.identificacao);
        const dadosAmostra = amostraCadastrada?.dados;
        const enderecoAmostra = amostraCadastrada?.endereco;

        return {
          identificacao: dep.identificacao,
          tipoImovel: dep.tipoImovel as TipoImovelAmostra,
          areaPrivativa: areaConstruida?.areaConstruida || comparativoDireto?.area || 0,
          areaTerreno: valorTerreno?.areaTerreno || 0,
          valorTotal: valorTerreno?.valorTotalAmostra || comparativoDireto?.valorAnuncio || 0,
          idadeAparente: dep.idadeAparente,
          estadoConservacao: dep.estadoConservacao as 'Novo' | 'Bom' | 'Regular' | 'Reparos Simples' | 'Reparos Importantes' | 'Sem Valor',
          cubBase: areaConstruida?.cubBase || 2500,
          ajusteAcabamento: areaConstruida?.ajusteAcabamento || 1.0,
          padrao: valorTerreno?.padrao || 'Médio',
          topografia: valorTerreno?.topografia || 'Terreno Plano',
          fonte: 'salvo',
          // Recupera dados de quantidade da amostra cadastrada (quartos, banheiros, vagas)
          quantidadeQuartos: dadosAmostra?.quantidadeQuartos || 0,
          quantidadeBanheiros: dadosAmostra?.quantidadeBanheiros || 0,
          quantidadeVagas: dadosAmostra?.quantidadeVagas || 0,
          // Recupera endereço e URL da amostra cadastrada
          endereco: enderecoAmostra?.rua || '',
          urlOriginal: dadosAmostra?.urlAnuncio,
          // Recupera seleção e ranking para homogeneização
          selecionadaHomogeneizacao: dadosAmostra?.selecionadaHomogeneizacao ?? true,
          rankingHomogeneizacao: dadosAmostra?.rankingHomogeneizacao,
          imagemUrl: dadosAmostra?.imagemUrl,
          externalId: amostraCadastrada?.externalId,
        } as AmostraPadrao;
      });

      // Garante que o Avaliando sempre exista mesmo quando o draft salvo
      // não contém a linha (caso contrário, o método evolutivo zera).
      const avaliandoReconstruido = amostrasReconstruidas.find(
        (a) => a.identificacao === 'Avaliando'
      );

      // Ordena pela ordem escolhida (ranking). As tabelas de cálculo são salvas
      // na ordem antiga após uma reordenação; sem este sort, reabrir o rascunho
      // inverteria a numeração das amostras em relação ao PDF.
      const demaisOrdenadas = amostrasReconstruidas
        .filter((a) => a.identificacao !== 'Avaliando')
        .sort(
          (a, b) =>
            (a.rankingHomogeneizacao ?? Number.MAX_SAFE_INTEGER) -
            (b.rankingHomogeneizacao ?? Number.MAX_SAFE_INTEGER)
        );

      return [avaliandoReconstruido ?? criarAvaliandoInicial(), ...demaisOrdenadas];
    }
    // Caso contrário, cria apenas o "Avaliando" inicial (necessário para sincronização)
    // Os valores serão preenchidos pelo useEffect que sincroniza com avaliacaoData
    return [criarAvaliandoInicial()];
  });

  // Ref para acessar amostrasLocais dentro de useEffect sem causar dependência circular
  const amostrasLocaisRef = useRef(amostrasLocais);

  // Mantém a ref atualizada sempre que amostrasLocais muda
  useEffect(() => {
    amostrasLocaisRef.current = amostrasLocais;
  }, [amostrasLocais]);

  // Refs para detectar mudanças externas nas tabelas (quando usuário edita diretamente)
  const lastSyncSignatureRef = useRef<string>('');

  // Estado para o modal de adicionar amostra manualmente
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addForm] = AntForm.useForm();

  // Estado para o modal do relatório IA
  const [isAiReportModalOpen, setIsAiReportModalOpen] = useState(false);

  // Estado para destacar um imóvel específico no mapa
  const [highlightedPropertyId, setHighlightedPropertyId] = useState<string | null>(null);

  // Callback para quando o usuário clica em um imóvel na vistoria visual do relatório IA
  const handlePropertyClickFromAiReport = useCallback((propertyId: string) => {
    // Fecha o modal
    setIsAiReportModalOpen(false);
    // Define o ID do imóvel para destacar no mapa
    setHighlightedPropertyId(propertyId);
    // Limpa o destaque após 5 segundos
    setTimeout(() => {
      setHighlightedPropertyId(null);
    }, 5000);
  }, []);

  // Handler para importar amostras do relatório IA
  const handleImportAiSamples = useCallback(async (autoImport = false) => {
    try {
      // Pega os IDs das top5 amostras do relatório IA
      const top5Ids = aiAnalysisResult?.analise?.top5_scraping?.map(s => s.id) || [];

      if (top5Ids.length === 0) {
        return false;
      }

      // Busca todas as propriedades do scraping
      const result = await inspectionService.getScrapedPropertiesByItemId(inspectionItemId);

      if (!result || !result.properties) {
        return false;
      }

      // Tenta filtrar por ID primeiro
      let top5Properties = result.properties.filter(p => top5Ids.includes(p.id));

      // Se não encontrou por ID, tenta por endereço (match mais flexível)
      if (top5Properties.length === 0) {
        const top5Data = aiAnalysisResult?.analise?.top5_scraping || [];

        // Para cada item do top5, busca a propriedade correspondente
        top5Properties = top5Data
          .map(top5Item => {
            // Busca a propriedade que melhor combina com este item do top5
            const match = result.properties.find(p =>
              p.address?.toLowerCase().includes(top5Item.endereco?.toLowerCase()) ||
              top5Item.endereco?.toLowerCase().includes(p.address?.toLowerCase())
            );
            return match;
          })
          .filter((p): p is NonNullable<typeof p> => p !== undefined); // Remove undefined
      }

      // Limita a exatamente 5 amostras (caso tenha vindo mais)
      top5Properties = top5Properties.slice(0, 5);

      if (top5Properties.length === 0) {
        return false;
      }

      // Converte ScrapedProperty para PropertyData
      const convertedProperties: PropertyData[] = top5Properties.map(prop => ({
        id_unico: prop.externalId || prop.id,
        titulo: prop.title,
        endereco_completo: prop.address,
        bairro: prop.district || '',
        cidade: prop.city || '',
        estado: prop.state || null,
        fonte: prop.source,
        score_similaridade: prop.score || 0,
        valor_venda: prop.price ? `R$ ${prop.price.toLocaleString('pt-BR')}` : null,
        valor_venda_num: prop.price || null,
        metragem: prop.areaUtil ? `${prop.areaUtil} m²` : null,
        metragem_num: prop.areaUtil || null,
        quartos: prop.bedrooms ? String(prop.bedrooms) : '0',
        quartos_num: prop.bedrooms || null,
        banheiros: prop.bathrooms ? String(prop.bathrooms) : '0',
        banheiros_num: prop.bathrooms || null,
        vagas: prop.parkingSpaces ? String(prop.parkingSpaces) : null,
        vagas_num: prop.parkingSpaces || null,
        latitude: prop.latitude,
        longitude: prop.longitude,
        distancia_metros: prop.distanceMeters || 0,
        url: prop.url || '',
        imagem_url: prop.imageUrl || null,
        terreno: prop.areaTotal || null,
        idade: undefined,
      }));

      // Usa a mesma lógica de onPropertiesSelected
      const amostrasExistentes = amostrasLocaisRef.current.filter(a => a.identificacao !== 'Avaliando');
      const qtdExistente = amostrasExistentes.length;

      // Converter PropertyData para AmostraPadrao
      const novasAmostras: AmostraPadrao[] = await Promise.all(convertedProperties.map(async (property, index) => {
        const maxId = amostrasLocaisRef.current.reduce((max, a) => {
          const match = a.identificacao ? a.identificacao.match(/#(\d+)/) : null;
          return match && match[1] ? Math.max(max, parseInt(match[1])) : max;
        }, 0);
        const novoId = `#${maxId + index + 1}`;

        const tituloLower = property.titulo?.toLowerCase() || '';
        let tipoImovel: 'Casa' | 'Apartamento' | 'Terreno' = 'Casa';

        if (tituloLower.includes('apartamento') || tituloLower.includes('apto') || tituloLower.includes('flat') || tituloLower.includes('cobertura') || tituloLower.includes('kitnet') || tituloLower.includes('studio') || tituloLower.includes('loft')) {
          tipoImovel = 'Apartamento';
        } else if (tituloLower.includes('terreno') || tituloLower.includes('lote') || tituloLower.includes('área') || tituloLower.includes('chácara') || tituloLower.includes('fazenda') || tituloLower.includes('sítio')) {
          tipoImovel = 'Terreno';
        }

        const rankingPosition = qtdExistente + index + 1;

        const propertyExternalId = property.id_unico || undefined;

        return {
          identificacao: novoId,
          tipoImovel,
          areaPrivativa: property.metragem_num || 0,
          areaTerreno: property.terreno || 0,
          valorTotal: property.valor_venda_num || 0,
          idadeAparente: property.idade ?? 10,
          estadoConservacao: 'Bom',
          quantidadeQuartos: property.quartos_num || 0,
          quantidadeBanheiros: property.banheiros_num || 0,
          quantidadeVagas: property.vagas_num || 0,
          cubBase: 2500,
          ajusteAcabamento: 1.0,
          padrao: 'Médio',
          topografia: tipoImovel === 'Apartamento' ? 'N/A' : 'Terreno Plano',
          endereco: property.endereco_completo || '',
          fonte: 'IA',
          urlOriginal: property.url,
          imagemUrl: property.imagem_url || undefined,
          externalId: propertyExternalId,
          selecionadaHomogeneizacao: true,
          rankingHomogeneizacao: rankingPosition,
        } as AmostraPadrao;
      }));

      const avaliando = amostrasLocaisRef.current.find(a => a.identificacao === 'Avaliando');
      const todasAmostras = avaliando
        ? [avaliando, ...amostrasExistentes, ...novasAmostras]
        : [...amostrasExistentes, ...novasAmostras];
      setAmostrasLocais(todasAmostras);

      if (!autoImport) {
        setIsAiReportModalOpen(false);
      }

      return true;
    } catch {
      return false;
    }
  }, [aiAnalysisResult, inspectionItemId]); // Usa amostrasLocaisRef.current ao invés de amostrasLocais para evitar loop

  // useEffect para importar automaticamente as amostras da IA quando disponíveis
  useEffect(() => {
    // IMPORTANTE: Verificamos o aiAnalysisResult diretamente ao invés do aiIsCompleted
    // porque o resultado pode existir mesmo sem o job registrado no banco
    const hasAnalysisResult = aiAnalysisResult?.analise?.status === 'success';
    const hasTop5 = !!aiAnalysisResult?.analise?.top5_scraping;
    const top5HasElements = (aiAnalysisResult?.analise?.top5_scraping?.length || 0) > 0;
    const notImportedYet = !aiSamplesAutoImported;

    // Condições para importação automática:
    // 1. Análise IA tem resultado com sucesso
    // 2. Tem top5_scraping disponível com elementos
    // 3. Ainda não importou automaticamente
    if (hasAnalysisResult && hasTop5 && top5HasElements && notImportedYet) {
      // Verifica se não tem amostras (exceto "Avaliando") - usa ref para evitar dependência circular
      const amostrasExistentes = amostrasLocaisRef.current.filter(a => a.identificacao !== 'Avaliando');

      if (amostrasExistentes.length === 0) {
        handleImportAiSamples(true).then(success => {
          if (success) {
            setAiSamplesAutoImported(true);
          }
        }).catch(() => {
        });
      }
    }
  }, [aiAnalysisResult, aiSamplesAutoImported, handleImportAiSamples]);

  // Determina metodologia padrão baseado no tipo do imóvel
  // Casa → Método Evolutivo; Apartamento/Terreno/Comercial → Comparativo Direto de Mercado
  const getMetodologiaPadrao = (tipo: string): string => {
    const tiposEvolutivo = ['Casa', 'Sobrado'];
    if (tiposEvolutivo.includes(tipo)) return 'Método Evolutivo';
    return 'Comparativo Direto de Mercado';
  };

  const tipoImovelInicial = avaliacaoData?.dadosImovel?.tipoImovel || identificacaoData?.tipoImovel || '';

  // Estado para Configuração e Fatoração
  const [metodologia, setMetodologia] = useState<string>(
    data.metodologia || getMetodologiaPadrao(tipoImovelInicial)
  );
  const [parametrosFatoracao, setParametrosFatoracao] = useState<string[]>(
    data.parametrosFatoracao || []
  );

  // Estado para comentários
  const [comentarios, setComentarios] = useState<string>(data.comentarios || '');

  // Sincroniza o valor padrão da metodologia com o state global quando o componente é montado
  // Isso garante que se o usuário não mexer no campo, o valor padrão será salvo
  useEffect(() => {
    if (!data.metodologia) {
      const metodologiaPadrao = getMetodologiaPadrao(tipoImovelInicial);
      setMetodologia(metodologiaPadrao);
      onUpdate({ metodologia: metodologiaPadrao });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executa apenas na montagem do componente

  // Estado para gerenciar alertas (usado internamente pelos cards)
  const [, setAlertasGlobais] = useState<AlertaItem[]>([]);
  const [, setAlertasHomogeneizacao] = useState<AlertaItem[]>([]);

  // Inicializa com dados mocados se estiver vazio
  const dadosAreaConstruida =
    data.dadosAreaConstruida.length > 0 ? data.dadosAreaConstruida : dadosAreaConstruidaMock;

  const dadosValorTerreno =
    data.dadosValorTerreno.length > 0 ? data.dadosValorTerreno : dadosValorTerrenoMock;

  const switchesTerreno =
    data.switchesTerreno.area !== undefined ? data.switchesTerreno : switchesTerrenoMock;

  const dadosDepreciacao =
    data.dadosDepreciacao.length > 0 ? data.dadosDepreciacao : dadosDepreciacaoMock;

  // Usa dados do Comparativo Direto diretamente do estado global
  // A sincronização com amostras é feita através do onUpdate da tabela
  const dadosComparativoDireto = data.dadosComparativoDireto || [];

  // SINCRONIZAÇÃO PRINCIPAL: Quando amostras mudam, atualiza TODAS as tabelas
  // Este useEffect é a fonte única de verdade para sincronizar amostras → tabelas
  useEffect(() => {
    // Gera uma "assinatura" do estado atual (amostras + tabelas)
    // Isso permite detectar mudanças tanto quando:
    // 1. amostrasLocais muda (nova amostra, edição, remoção)
    // 2. Tabelas mudam externamente (usuário edita diretamente na tabela)
    const currentSignature = JSON.stringify({
      amostras: amostrasLocais.map(a =>
        `${a.identificacao}:${a.tipoImovel}:${a.areaPrivativa}:${a.valorTotal}:${a.areaTerreno}:${a.selecionadaHomogeneizacao}:${a.idadeAparente}:${a.estadoConservacao}`
      ),
      tabelas: {
        depreciacao: dadosDepreciacao.map(d => `${d.identificacao}:${d.idadeAparente}:${d.estadoConservacao}`),
        areaConstruida: dadosAreaConstruida.map(d => `${d.identificacao}:${d.areaConstruida}`),
        valorTerreno: dadosValorTerreno.map(d => `${d.amostra}:${d.areaTerreno}:${d.valorTotalAmostra}`),
        comparativoDireto: dadosComparativoDireto.map(d => `${d.amostra}:${d.area}:${d.valorAnuncio}`),
      }
    });

    // Se a assinatura não mudou, não precisa sincronizar
    if (currentSignature === lastSyncSignatureRef.current) {
      return;
    }

    // Verifica se precisa sincronizar comparando IDs e quantidade de amostras
    const idsAmostrasAtuais = new Set(amostrasLocais.map(a => a.identificacao));
    const idsDepreciacao = new Set(dadosDepreciacao.map(d => d.identificacao));
    const idsAreaConstruida = new Set(dadosAreaConstruida.map(d => d.identificacao));
    const idsValorTerreno = new Set(dadosValorTerreno.map(d => d.amostra));
    const idsComparativoDireto = new Set(dadosComparativoDireto.map(d => d.amostra));

    // Sincroniza se:
    // 1. Quantidade mudou em qualquer tabela (adição/remoção)
    // 2. Algum ID novo apareceu
    // 3. Algum ID antigo sumiu
    // Sincroniza se:
    // 1. Quantidade mudou em qualquer tabela (adição/remoção)
    // 2. Algum ID novo apareceu
    // 3. Algum ID antigo sumiu
    const quantidadeMudou =
      amostrasLocais.length !== dadosDepreciacao.length ||
      amostrasLocais.filter(a => a.tipoImovel === 'Apartamento').length !== idsAreaConstruida.size ||
      amostrasLocais.length !== idsValorTerreno.size ||
      amostrasLocais.filter(a => a.tipoImovel !== 'Terreno').length !== idsComparativoDireto.size;

    const temNovoId = Array.from(idsAmostrasAtuais).some(id => !idsDepreciacao.has(id));
    const temIdRemovido = Array.from(idsDepreciacao).some(id => !idsAmostrasAtuais.has(id));

    // Verifica se algum VALOR mudou nas amostras (área, valor, etc)
    const valoresMudaram = amostrasLocais.some(amostra => {
      const dadoTabela = dadosComparativoDireto.find(d => d.amostra === amostra.identificacao);
      if (!dadoTabela) return false;
      return dadoTabela.area !== (amostra.areaPrivativa || 0) ||
        dadoTabela.valorAnuncio !== (amostra.valorTotal || 0);
    });

    const idadeOuEstadoMudaram = amostrasLocais.some(amostra => {
      if (amostra.identificacao === 'Avaliando') return false;
      const dadoDep = dadosDepreciacao.find(d => d.identificacao === amostra.identificacao);
      if (!dadoDep) return false;
      return dadoDep.idadeAparente !== (Number(amostra.idadeAparente) || 0) ||
        dadoDep.estadoConservacao !== amostra.estadoConservacao;
    });

    const precisaSincronizar = quantidadeMudou || temNovoId || temIdRemovido || valoresMudaram || idadeOuEstadoMudaram;

    if (!precisaSincronizar) {
      // Atualiza a assinatura mesmo sem sincronizar para evitar checks futuros desnecessários
      lastSyncSignatureRef.current = currentSignature;
      return;
    }

    // 1. Atualiza Tabela de Depreciação
    const novosDadosDepreciacao: DadosDepreciacao[] = amostrasLocais.map((amostra) => {
      const existente = dadosDepreciacao.find((d) => d.identificacao === amostra.identificacao);

      // Merge: atualiza valores da amostra, mantém cálculos existentes.
      // Avaliando: idade/estado vêm da etapa Avaliação (effect dedicado mantém o `existente`
      // fresco), então preservamos o valor existente para não reverter a idade do imóvel principal.
      const isAvaliando = amostra.identificacao === 'Avaliando';
      return {
        identificacao: amostra.identificacao,
        tipoImovel: amostra.tipoImovel,
        idadeAparente: isAvaliando
          ? (existente?.idadeAparente ?? Number(amostra.idadeAparente) ?? 0)
          : (Number(amostra.idadeAparente) || 0),
        estadoConservacao: isAvaliando
          ? (existente?.estadoConservacao ?? amostra.estadoConservacao)
          : amostra.estadoConservacao,
        vidaUtil: amostra.tipoImovel === 'Apartamento' ? 60 : 70,
        valorResidual: existente?.valorResidual ?? 0.2,
        // Mantém cálculos se existir, senão usa default
        coefHeidecke: existente?.coefHeidecke ?? 0,
        porcentagemVU: existente?.porcentagemVU ?? 0,
        depreciacao: existente?.depreciacao ?? 0,
      };
    });

    // 2. Atualiza Tabela de Área Construída (exclui Terreno)
    const novosDadosAreaConstruida: DadosAreaConstruida[] = amostrasLocais
      .filter((a) => a.tipoImovel !== 'Terreno')
      .map((amostra) => {
        const existente = dadosAreaConstruida.find((d) => d.identificacao === amostra.identificacao);

        // Valores base da amostra
        const areaConstruida = Number(amostra.areaPrivativa) || 0;
        // cubBase e ajusteAcabamento são editáveis diretamente na Tabela de Área
        // Construída (gravados apenas em dadosAreaConstruida, não em amostrasLocais).
        // Ao reconstruir após trocar/substituir uma amostra, preserva o valor que o
        // usuário digitou na tabela (existente) em vez de resetar para o default da amostra.
        const cubBase = Number(existente?.cubBase ?? amostra.cubBase) || 2500;
        const ajusteAcabamento = Number(existente?.ajusteAcabamento ?? amostra.ajusteAcabamento) || 1.0;

        // Mantém depreciação (vem da tabela de depreciação)
        const depreciacao = existente?.depreciacao ?? 0;

        // Recalcula valores derivados
        const cubAjustado = cubBase * ajusteAcabamento;
        const valorFinal = areaConstruida * cubAjustado * (1 - depreciacao / 100);

        return {
          identificacao: amostra.identificacao,
          tipoImovel: amostra.tipoImovel,
          areaConstruida,
          cubBase,
          ajusteAcabamento,
          depreciacao,
          cubAjustado,
          valorFinal,
        };
      });

    // 3. Atualiza Tabela de Valor do Terreno
    const novosDadosValorTerreno: DadosValorTerreno[] = amostrasLocais.map((amostra) => {
      const existente = dadosValorTerreno.find((d) => d.amostra === amostra.identificacao);

      // Valores base da amostra
      const areaTerreno = Number(amostra.areaTerreno) || 0;
      const valorTotalAmostra = Number(amostra.valorTotal) || 0;

      // Recalcula valores baseados nas propriedades da amostra
      const valorTerreno = valorTotalAmostra;
      const valorUnitario = areaTerreno > 0 ? valorTerreno / areaTerreno : 0;

      // Mantém fatores configurados pelo usuário (ou usa defaults)
      const indiceFiscal = existente?.indiceFiscal ?? 1.0;
      const fat = existente?.fat ?? 0;

      // Recalcula valor final com base nos fatores
      const valorUnitarioFinal = valorUnitario * fat * indiceFiscal;

      // Avaliando não é amostra de mercado: fatorOferta = 1.0 (sem desconto).
      const defaultFatorOferta = amostra.identificacao === 'Avaliando' ? 1.0 : 0.9;

      return {
        amostra: amostra.identificacao,
        tipoImovel: amostra.tipoImovel,
        areaTerreno,
        padrao: amostra.padrao || 'Médio',
        topografia: amostra.topografia || 'Terreno Plano',
        valorTotalAmostra,
        // Valores recalculados
        valorTerreno,
        valorUnitario,
        valorUnitarioFinal,
        // Fatores configurados (preservados)
        indiceFiscal,
        fatorOferta: existente?.fatorOferta ?? defaultFatorOferta,
        fatorTopografia: existente?.fatorTopografia ?? (amostra.topografia === 'Terreno Plano' ? 1.0 : 0.97),
        fatorArea: existente?.fatorArea ?? 1.0,
        fatorTestada: existente?.fatorTestada ?? 1.0,
        fatorVegetacao: existente?.fatorVegetacao ?? 1.0,
        fatorExtraI: existente?.fatorExtraI ?? 1.0,
        fatorExtraII: existente?.fatorExtraII ?? 1.0,
        fatorExtraIII: existente?.fatorExtraIII ?? 1.0,
        fat,
      };
    });

    // 4. Atualiza Tabela de Comparativo Direto (inclui Terreno usando areaTerreno)
    const novosDadosComparativoDireto = amostrasLocais.map((amostra) => {
      const existente = dadosComparativoDireto.find((d) => d.amostra === amostra.identificacao);

      // SEMPRE recalcula area, valorAnuncio e valorM2 (dependem da amostra)
      // Para terrenos, a área de referência é a do terreno; para os demais, a área privativa
      const area = amostra.tipoImovel === 'Terreno'
        ? (amostra.areaTerreno || 0)
        : (amostra.areaPrivativa || 0);
      const valorAnuncio = amostra.valorTotal || 0;
      const valorM2 = area > 0 && valorAnuncio > 0 ? valorAnuncio / area : 0;

      // Avaliando não é amostra de mercado: fatorOferta = 1.0 (sem desconto).
      const defaultFatorOferta = amostra.identificacao === 'Avaliando' ? 1.0 : 0.9;
      const fatorOferta = existente?.fatorOferta ?? defaultFatorOferta;
      const fatorRegiao = existente?.fatorRegiao ?? 1.0;
      const fatorAndar = existente?.fatorAndar ?? 1.0;
      const fatorVaga = existente?.fatorVaga ?? 1.0;
      const fatorArea = existente?.fatorArea ?? 1.0;
      const fatorPadraoAcabamento = existente?.fatorPadraoAcabamento ?? 1.0;
      const fatorEstadoConservacao = existente?.fatorEstadoConservacao ?? 1.0;
      const fatorExtraI = existente?.fatorExtraI ?? 1.0;
      const fatorExtraII = existente?.fatorExtraII ?? 1.0;
      const fatorExtraIII = existente?.fatorExtraIII ?? 1.0;

      // FAT = soma de desvios dos fatores de homogeneização (sem oferta).
      // Oferta é aplicada por fora, alinhando com a Tabela de Valor de Terreno.
      const fat = 1.0 +
        (fatorRegiao - 1.0) +
        (fatorAndar - 1.0) +
        (fatorVaga - 1.0) +
        (fatorArea - 1.0) +
        (fatorPadraoAcabamento - 1.0) +
        (fatorEstadoConservacao - 1.0) +
        (fatorExtraI - 1.0) +
        (fatorExtraII - 1.0) +
        (fatorExtraIII - 1.0);
      // Avaliando não recebe fator de oferta (não é amostra de mercado).
      const ofertaAplicada = amostra.identificacao === 'Avaliando' ? 1.0 : fatorOferta;
      const valorHomogeneizado = valorM2 * ofertaAplicada * fat;

      return {
        amostra: amostra.identificacao,
        area,
        valorAnuncio,
        valorM2,
        fatorOferta,
        fatorRegiao,
        fatorAndar,
        fatorVaga,
        fatorArea,
        fatorPadraoAcabamento,
        fatorEstadoConservacao,
        fatorExtraI,
        fatorExtraII,
        fatorExtraIII,
        fat,
        valorHomogeneizado,
      };
    });

    // Propaga todas as atualizações de uma vez
    onUpdate({
      dadosDepreciacao: novosDadosDepreciacao,
      dadosAreaConstruida: novosDadosAreaConstruida,
      dadosValorTerreno: novosDadosValorTerreno,
      dadosComparativoDireto: novosDadosComparativoDireto,
    });

    // 🎯 Atualiza a assinatura APÓS a sincronização para evitar loops
    // Na próxima execução, se o estado não mudou, não sincroniza novamente
    lastSyncSignatureRef.current = currentSignature;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Rastreia IDs, tipos E TODOS os valores para detectar qualquer mudança nas amostras
    // IMPORTANTE: Inclui TODOS os campos editáveis para reagir a qualquer edição
    amostrasLocais.map((a) =>
      `${a.identificacao}:${a.tipoImovel}:${a.areaPrivativa}:${a.valorTotal}:${a.areaTerreno}:${a.quantidadeQuartos}:${a.quantidadeBanheiros}:${a.quantidadeVagas}:${a.selecionadaHomogeneizacao}:${a.cubBase}:${a.ajusteAcabamento}:${a.idadeAparente}:${a.estadoConservacao}:${a.padrao}:${a.topografia}`
    ).join(','),
    // 🔑 IMPORTANTE: Adiciona as tabelas como dependências para detectar edições diretas
    // Quando o usuário edita uma amostra (ex: muda área), isso atualiza a tabela
    // e precisamos propagar essa mudança para as outras tabelas
    dadosDepreciacao.length,
    dadosAreaConstruida.length,
    dadosValorTerreno.length,
    dadosComparativoDireto.length,
    // Serializa valores essenciais para detectar mudanças nos dados (não apenas quantidade)
    JSON.stringify(dadosDepreciacao.map(d => `${d.identificacao}:${d.idadeAparente}`)),
    JSON.stringify(dadosAreaConstruida.map(d => `${d.identificacao}:${d.areaConstruida}`)),
    JSON.stringify(dadosValorTerreno.map(d => `${d.amostra}:${d.areaTerreno}:${d.valorTotalAmostra}`)),
    JSON.stringify(dadosComparativoDireto.map(d => `${d.amostra}:${d.area}:${d.valorAnuncio}`)),
  ]);

  // Normaliza rascunhos com numeração fora de sequência (ex.: #4, #5 após
  // exclusões ou importações antigas): renumera amostras E tabelas de cálculo
  // juntas, uma única vez na montagem, para que a lista de amostras, as tabelas
  // de edição dos cálculos e o PDF usem a mesma numeração #1..#N.
  const renumeracaoInicialRef = useRef(false);
  useEffect(() => {
    if (renumeracaoInicialRef.current) return;
    renumeracaoInicialRef.current = true;

    const { amostras: renumeradas, idMapping } = renumerarAmostrasSequencialmente(amostrasLocais);
    if (Object.keys(idMapping).length === 0) return;

    setAmostrasLocais(renumeradas);
    onUpdate(
      renomearIdsNasTabelas(idMapping, {
        dadosDepreciacao,
        dadosAreaConstruida,
        dadosValorTerreno,
        dadosComparativoDireto,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sincroniza amostrasLocais com wizardData.amostras.amostras para persistência
  // Isso garante que os dados de quantidade (quartos, banheiros, vagas) sejam salvos
  useEffect(() => {
    // Converte AmostraPadrao[] para Amostra[] (formato do wizardData)
    const amostrasConvertidas = amostrasLocais
      .filter(a => a.identificacao !== 'Avaliando') // Remove o Avaliando
      .map((amostra) => {
        return {
          id: amostra.identificacao,
          externalId: amostra.externalId,
          endereco: {
            rua: amostra.endereco || '',
            numero: '',
            bairro: '',
            cidade: '',
            uf: '',
            cep: '',
          },
          imagemUrl: amostra.imagemUrl,
          dados: {
            tipoImovel: amostra.tipoImovel,
            areaPrivativa: amostra.areaPrivativa,
            quantidadeQuartos: amostra.quantidadeQuartos || 0,
            quantidadeBanheiros: amostra.quantidadeBanheiros || 0,
            quantidadeVagas: amostra.quantidadeVagas || 0,
            padraoAcabamento: amostra.padrao || 'Normal',
            estadoConservacao: amostra.estadoConservacao,
            valorAnunciado: amostra.valorTotal,
            valorM2: amostra.areaPrivativa > 0 ? amostra.valorTotal / amostra.areaPrivativa : 0,
            fonteAnuncio: amostra.fonte || 'manual',
            urlAnuncio: amostra.urlOriginal,
            dataAnuncio: new Date().toISOString(),
            selecionadaHomogeneizacao: amostra.selecionadaHomogeneizacao ?? true,
            rankingHomogeneizacao: amostra.rankingHomogeneizacao,
            imagemUrl: amostra.imagemUrl,
          },
        };
      });

    onUpdate({ amostras: amostrasConvertidas as any });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amostrasLocais]);

  // Sincroniza o tipo de imóvel do "Avaliando" com a seção de Avaliação e Identificação
  useEffect(() => {
    const tipoImovelAvaliacao = avaliacaoData.dadosImovel.tipoImovel as
      | 'Casa'
      | 'Apartamento'
      | 'Terreno';
    // Usa área construída do step Avaliação ou área privativa do step Identificação
    const areaConstruidaAvaliacao = avaliacaoData.dadosImovel.areaConstruida;
    const areaPrivativaIdentificacao = identificacaoData?.areaPrivativa;
    const areaTerrenoAvaliacao = avaliacaoData.dadosTerreno?.areaTerreno;

    // Busca o "Avaliando" atual
    const avaliaExistente = amostrasLocais.find((a) => a.identificacao === 'Avaliando');

    // Rede de segurança: se o Avaliando sumiu do estado (draft corrompido),
    // recria a linha para que o método evolutivo volte a funcionar.
    if (!avaliaExistente) {
      setAmostrasLocais([criarAvaliandoInicial(), ...amostrasLocais]);
      return;
    }

    // Verifica se realmente precisa atualizar
    const precisaAtualizar =
      avaliaExistente.tipoImovel !== (tipoImovelAvaliacao || 'Casa') ||
      (areaConstruidaAvaliacao && avaliaExistente.areaPrivativa !== areaConstruidaAvaliacao) ||
      (areaTerrenoAvaliacao && avaliaExistente.areaTerreno !== areaTerrenoAvaliacao);

    if (!precisaAtualizar) return;

    // Atualiza apenas se houver mudança real
    const novasAmostras = amostrasLocais.map((amostra) => {
      if (amostra.identificacao === 'Avaliando') {
        return {
          ...amostra,
          tipoImovel: tipoImovelAvaliacao || 'Casa',
          areaPrivativa: areaConstruidaAvaliacao || areaPrivativaIdentificacao || amostra.areaPrivativa,
          areaTerreno: areaTerrenoAvaliacao || amostra.areaTerreno,
        };
      }
      return amostra;
    });

    setAmostrasLocais(novasAmostras);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    avaliacaoData.dadosImovel.tipoImovel,
    avaliacaoData.dadosImovel.areaConstruida,
    avaliacaoData.dadosTerreno?.areaTerreno,
    identificacaoData?.areaPrivativa,
  ]);

  // Sincroniza dados do "Avaliando" com o step de Avaliação (step 3) e Identificação
  useEffect(() => {
    const { dadosImovel, dadosTerreno } = avaliacaoData;
    // Step Avaliação é fonte única para área construída do "Avaliando" (sincroniza tabela).
    // ?? respeita 0 explícito (imóvel 100% não averbado).
    const areaConstruidaFinal = dadosImovel.areaConstruida ?? 0;
    const areaTerrenoFinal = dadosTerreno?.areaTerreno || 0;

    // Atualiza Tabela de Depreciação - linha "Avaliando"
    const novosDadosDepreciacao = dadosDepreciacao.map((item: DadosDepreciacao) => {
      if (item.identificacao === 'Avaliando') {
        return {
          ...item,
          idadeAparente: dadosImovel.idadeAparente || 0,
          estadoConservacao: dadosImovel.estadoConservacao || 'Regular',
          tipoImovel: (dadosImovel.tipoImovel as TipoImovelAmostra) || 'Casa',
        };
      }
      return item;
    });

    // Atualiza Tabela de Área Construída - linha "Avaliando"
    const novosDadosAreaConstruida = dadosAreaConstruida.map((item) => {
      if (item.identificacao === 'Avaliando') {
        const cubAjustado = item.cubAjustado || 0;
        const dep = item.depreciacao || 0;
        return {
          ...item,
          areaConstruida: areaConstruidaFinal,
          tipoImovel: (dadosImovel.tipoImovel as TipoImovelAmostra) || 'Casa',
          valorFinal: areaConstruidaFinal * cubAjustado * (1 - dep / 100),
        };
      }
      return item;
    });

    // Atualiza Tabela de Valor do Terreno - linha "Avaliando"
    const novosDadosValorTerreno = dadosValorTerreno.map((item) => {
      if (item.amostra === 'Avaliando') {
        return {
          ...item,
          tipoImovel: (dadosImovel.tipoImovel as TipoImovelAmostra) || 'Casa',
          areaTerreno: areaTerrenoFinal,
        };
      }
      return item;
    });

    // Verifica se houve mudanças antes de atualizar
    const mudouDepreciacao =
      JSON.stringify(novosDadosDepreciacao) !== JSON.stringify(dadosDepreciacao);
    const mudouAreaConstruida =
      JSON.stringify(novosDadosAreaConstruida) !== JSON.stringify(dadosAreaConstruida);
    const mudouValorTerreno =
      JSON.stringify(novosDadosValorTerreno) !== JSON.stringify(dadosValorTerreno);

    if (mudouDepreciacao || mudouAreaConstruida || mudouValorTerreno) {
      onUpdate({
        dadosDepreciacao: novosDadosDepreciacao,
        dadosAreaConstruida: novosDadosAreaConstruida,
        dadosValorTerreno: novosDadosValorTerreno,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    avaliacaoData.dadosImovel.idadeAparente,
    avaliacaoData.dadosImovel.estadoConservacao,
    avaliacaoData.dadosImovel.tipoImovel,
    avaliacaoData.dadosImovel.areaConstruida,
    avaliacaoData.dadosTerreno?.areaTerreno,
    identificacaoData?.areaPrivativa,
  ]);

  // Sincroniza dados do "Avaliando" na tabela de Comparativo Direto
  // Atualiza área e recalcula valorM2 quando dados de Avaliação/Identificação mudam
  useEffect(() => {
    // Só sincroniza se estivermos no método Comparativo Direto
    if (metodologia !== 'Comparativo Direto de Mercado') return;

    const areaConstruidaAvaliacao = avaliacaoData.dadosImovel.areaConstruida;
    const areaPrivativaIdentificacao = identificacaoData?.areaPrivativa;
    const areaFinal = areaConstruidaAvaliacao || areaPrivativaIdentificacao || 0;

    // Busca o Avaliando atual na tabela
    const avaliaExistente = dadosComparativoDireto.find((d) => d.amostra === 'Avaliando');
    if (!avaliaExistente) return;

    // Verifica se a área E o valorM2 derivado já estão consistentes.
    // Só pular se ambos estão corretos — guarda de área isolada deixava valorM2
    // dessincronizado quando initialData vinha congelado com área antiga.
    const valorAnuncioAtual = avaliaExistente.valorAnuncio || 0;
    const expectedValorM2 = areaFinal > 0 && valorAnuncioAtual > 0
      ? valorAnuncioAtual / areaFinal
      : 0;
    const areaCerta = Math.abs(avaliaExistente.area - areaFinal) < 0.01;
    const valorM2Certo = Math.abs((avaliaExistente.valorM2 || 0) - expectedValorM2) < 0.01;
    if (areaCerta && valorM2Certo) return;

    // Atualiza o Avaliando com a nova área e recalcula valores.
    // Avaliando não recebe fator de oferta (não é amostra de mercado): FAT = 1.0.
    const novosDadosComparativoDireto = dadosComparativoDireto.map((item) => {
      if (item.amostra === 'Avaliando') {
        const novaArea = areaFinal;
        const valorAnuncio = item.valorAnuncio || 0;
        const valorM2 = novaArea > 0 && valorAnuncio > 0 ? valorAnuncio / novaArea : 0;
        const fat = 1.0;
        const valorHomogeneizado = valorM2 * fat;

        return {
          ...item,
          area: novaArea,
          valorM2,
          fat,
          valorHomogeneizado,
        };
      }
      return item;
    });

    onUpdate({ dadosComparativoDireto: novosDadosComparativoDireto });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    avaliacaoData.dadosImovel.areaConstruida,
    identificacaoData?.areaPrivativa,
    metodologia,
  ]);

  // Sincroniza valores de depreciação da TabelaDepreciacao para TabelaAreaConstruida
  // FLUXO: TabelaDepreciacao calcula → AmostrasStep sincroniza → TabelaAreaConstruida exibe
  useEffect(() => {
    // Atualizar depreciação na tabela de área construída baseado na tabela de depreciação
    // Busca o valor de depreciação calculado para cada amostra pelo identificacao (#1, #2, Avaliando, etc.)
    const novosDadosAreaConstruida = dadosAreaConstruida.map((itemArea) => {
      const itemDepreciacao = dadosDepreciacao.find(
        (dep: DadosDepreciacao) => dep.identificacao === itemArea.identificacao
      );
      return {
        ...itemArea,
        depreciacao: itemDepreciacao?.depreciacao || 0,
      };
    });

    // Só atualiza se houver mudança real nos valores de depreciação
    const precisaAtualizar = novosDadosAreaConstruida.some((item, index) => {
      const itemAnterior = dadosAreaConstruida[index];
      return itemAnterior && Math.abs(item.depreciacao - itemAnterior.depreciacao) > 0.0001;
    });

    if (precisaAtualizar) {
      onUpdate({ dadosAreaConstruida: novosDadosAreaConstruida });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dadosDepreciacao]); // Re-executa sempre que a depreciação for recalculada

  // Callback quando a tabela de depreciação é atualizada
  // Atualiza automaticamente a coluna "Depreciação" na tabela de área construída
  const handleDepreciacaoUpdate = (novosDadosDepreciacao: DadosDepreciacao[]) => {
    // Merge: atualiza apenas as amostras que vieram do filtro, mantendo as demais intactas
    const dadosDepreciacaoMerged = dadosDepreciacao.map((itemOriginal) => {
      const itemAtualizado = novosDadosDepreciacao.find(
        (dep) => dep.identificacao === itemOriginal.identificacao
      );
      return itemAtualizado || itemOriginal;
    });
    onUpdate({ dadosDepreciacao: dadosDepreciacaoMerged });

    // Atualizar depreciação na tabela de área construída para cada amostra correspondente
    const novosDadosAreaConstruida = dadosAreaConstruida.map((itemArea) => {
      const depreciacao = dadosDepreciacaoMerged.find(
        (dep) => dep.identificacao === itemArea.identificacao
      );
      return {
        ...itemArea,
        depreciacao: depreciacao?.depreciacao ?? itemArea.depreciacao ?? 0,
      };
    });

    onUpdate({ dadosAreaConstruida: novosDadosAreaConstruida });
  };

  // Filtra dados para análise estatística:
  // 1. Remove "Avaliando" (não tem valor de mercado)
  // 2. Remove "Apartamento" (não tem terreno individual)
  // 3. Inclui apenas amostras selecionadas para homogeneização
  // As estatísticas devem usar APENAS amostras de comparação que têm terreno e estão selecionadas
  const dadosValorTerrenoComparacao = useMemo(() => {
    const resultado = dadosValorTerreno.filter((item) => {
      if (item.amostra === 'Avaliando' || item.tipoImovel === 'Apartamento') return false;
      // Verifica se a amostra está selecionada para homogeneização
      const amostra = amostrasLocais.find(a => a.identificacao === item.amostra);
      return amostra?.selecionadaHomogeneizacao === true;
    });

    return resultado;
  }, [dadosValorTerreno, amostrasLocais]);

  // Filtra dados do Comparativo Direto para análise (remove "Avaliando" e amostras não selecionadas)
  const dadosComparativoDiretoComparacao = useMemo(() => {
    // Filtra removendo "Avaliando" e apenas incluindo amostras selecionadas
    const resultado = dadosComparativoDireto.filter((item) => {
      if (item.amostra === 'Avaliando') return false;
      // Verifica se a amostra está selecionada para homogeneização
      const amostra = amostrasLocais.find(a => a.identificacao === item.amostra);
      return amostra?.selecionadaHomogeneizacao === true;
    });

    return resultado;
  }, [dadosComparativoDireto, amostrasLocais]);

  const isAmostraSelecionada = useCallback((identificacao: string) => {
    if (identificacao === 'Avaliando') return true;
    const amostra = amostrasLocais.find(a => a.identificacao === identificacao);
    return amostra?.selecionadaHomogeneizacao === true;
  }, [amostrasLocais]);

  // Ordem de exibição das linhas nas tabelas de cálculo = ordem das amostras
  // (Avaliando primeiro). Após reordenar amostras, as tabelas mantêm a ordem
  // antiga das linhas internamente; sem este sort, a numeração exibida ficaria
  // fora de ordem e divergiria da ordem renumerada do PDF.
  const ordemAmostras = useMemo(() => {
    const ordem = new Map<string, number>();
    amostrasLocais.forEach((a, idx) => ordem.set(a.identificacao, idx));
    return ordem;
  }, [amostrasLocais]);

  const ordenarPorAmostra = useCallback(<T,>(itens: T[], getId: (item: T) => string): T[] => {
    return [...itens].sort(
      (a, b) =>
        (ordemAmostras.get(getId(a)) ?? Number.MAX_SAFE_INTEGER) -
        (ordemAmostras.get(getId(b)) ?? Number.MAX_SAFE_INTEGER)
    );
  }, [ordemAmostras]);

  const dadosComparativoDiretoFiltrado = useMemo(() =>
    ordenarPorAmostra(dadosComparativoDireto.filter(d => isAmostraSelecionada(d.amostra)), d => d.amostra),
    [dadosComparativoDireto, isAmostraSelecionada, ordenarPorAmostra]
  );

  const dadosValorTerrenoFiltrado = useMemo(() =>
    ordenarPorAmostra(dadosValorTerreno.filter(d => isAmostraSelecionada(d.amostra)), d => d.amostra),
    [dadosValorTerreno, isAmostraSelecionada, ordenarPorAmostra]
  );

  const dadosAreaConstruidaFiltrado = useMemo(() =>
    ordenarPorAmostra(dadosAreaConstruida.filter(d => isAmostraSelecionada(d.identificacao)), d => d.identificacao),
    [dadosAreaConstruida, isAmostraSelecionada, ordenarPorAmostra]
  );

  const dadosDepreciacaoFiltrado = useMemo(() =>
    ordenarPorAmostra(dadosDepreciacao.filter(d => isAmostraSelecionada(d.identificacao)), d => d.identificacao),
    [dadosDepreciacao, isAmostraSelecionada, ordenarPorAmostra]
  );

  // Mapa de renumeração: ID original → #1, #2, #3... (apenas amostras selecionadas)
  // Usado nas colunas das tabelas para exibir numeração sequencial sem alterar os dados
  const displayIdMap = useMemo(() => {
    const map: Record<string, string> = { 'Avaliando': 'Avaliando' };
    let contador = 0;
    amostrasLocais
      .filter(a => a.identificacao !== 'Avaliando' && a.selecionadaHomogeneizacao)
      .forEach(a => {
        map[a.identificacao] = `#${++contador}`;
      });
    return map;
  }, [amostrasLocais]);

  // Calcula valor homogeneizado saneado para Comparativo Direto (após remoção de outliers)
  const valorHomogeneizadoSaneadoComparativoDireto = useMemo(() => {
    const valoresHomogeneizados = dadosComparativoDiretoComparacao
      .map((item) => item.valorHomogeneizado)
      .filter((v) => v > 0);

    if (valoresHomogeneizados.length === 0) return 0;

    // Retorna a média de TODAS as amostras válidas
    // Os limites (±30%) são critério NBR 14653 apenas para indicação visual
    return valoresHomogeneizados.reduce((acc, val) => acc + val, 0) / valoresHomogeneizados.length;
  }, [dadosComparativoDiretoComparacao]);

  // Calcula o valor unitário médio com TODAS as amostras válidas
  // Limites de ±30% são apenas para visualização, não para exclusão de dados
  const valorUnitarioSaneado = useMemo(() => {
    // 1. Filtra apenas valores válidos (> 0)
    const valoresValidos = dadosValorTerrenoComparacao
      .map((item) => item.valorUnitarioFinal)
      .filter((valor) => valor > 0);

    const n = valoresValidos.length;

    if (n === 0) {
      return 0;
    }

    // 2. Calcula a média com TODAS as amostras válidas
    // Os limites (±30%) são critério NBR 14653 apenas para indicação visual
    return valoresValidos.reduce((acc, val) => acc + val, 0) / n;
  }, [dadosValorTerrenoComparacao]);

  // Pega a área do terreno do imóvel "Avaliando"
  // Prioriza dados da tabela, mas usa avaliacaoData como fallback
  const areaImovelAvaliando = useMemo(() => {
    const avaliando = dadosValorTerreno.find((item) => item.amostra === 'Avaliando');
    const areaFromTable = avaliando?.areaTerreno || 0;
    // Se a tabela tiver valor 0, tenta pegar do avaliacaoData
    if (areaFromTable === 0) {
      return avaliacaoData?.dadosTerreno?.areaTerreno || 0;
    }
    return areaFromTable;
  }, [dadosValorTerreno, avaliacaoData?.dadosTerreno?.areaTerreno]);

  // Pega a área construída do imóvel "Avaliando" do step Avaliação (fonte única).
  // ?? em vez de || para respeitar 0 explícito digitado pelo analista (imóvel 100% não
  // averbado tem area averbada = 0 — não pode cair em fallback que ressuscita o valor
  // herdado da solicitação original).
  const areaConstruidaAvaliando = useMemo(() => {
    return avaliacaoData?.dadosImovel?.areaConstruida ?? 0;
  }, [avaliacaoData?.dadosImovel?.areaConstruida]);

  // Pega o valor unitário final da construção (CUB Ajustado) do imóvel "Avaliando".
  // Fonte única compartilhada com o PDF (transformLaudoData) — recalcula do cubBase
  // quando o cubAjustado salvo é 0, evitando que o valor não averbado zere.
  const valorUnitarioConstrucao = useMemo(() => {
    return getValorUnitarioConstrucaoAvaliando(dadosAreaConstruida);
  }, [dadosAreaConstruida]);

  // Cria um Map com os valores totais das amostras para cálculo do CUB de apartamentos
  const valoresTotaisAmostras = useMemo(() => {
    const map = new Map<string, number>();
    dadosValorTerreno.forEach((item) => {
      map.set(item.amostra, item.valorTotalAmostra || 0);
    });
    return map;
  }, [dadosValorTerreno]);

  // Handler para editar amostra
  const handleEditAmostra = (amostraEditada: AmostraPadrao) => {
    // APENAS atualiza amostrasLocais
    // O useEffect de sincronização vai cuidar de atualizar todas as tabelas automaticamente
    const novasAmostras = amostrasLocais.map((amostra) =>
      amostra.identificacao === amostraEditada.identificacao ? amostraEditada : amostra
    );
    setAmostrasLocais(novasAmostras);
    // As tabelas serão atualizadas automaticamente pelo useEffect de sincronização
  };

  // Função para gerar o próximo ID de amostra
  const getNextAmostraId = useCallback(() => {
    const maxId = amostrasLocais.reduce((max, a) => {
      const match = a.identificacao ? a.identificacao.match(/#(\d+)/) : null;
      return match && match[1] ? Math.max(max, parseInt(match[1])) : max;
    }, 0);
    return `#${maxId + 1}`;
  }, [amostrasLocais]);

  // Valores padrão para nova amostra
  const getDefaultAmostra = useCallback((): AmostraPadrao => ({
    identificacao: getNextAmostraId(),
    tipoImovel: 'Casa',
    areaPrivativa: undefined as unknown as number,
    areaTerreno: undefined as unknown as number,
    valorTotal: undefined as unknown as number,
    idadeAparente: undefined as unknown as number,
    estadoConservacao: 'Bom',
    cubBase: 2500,
    ajusteAcabamento: 1.0,
    padrao: 'Médio',
    topografia: 'Terreno Plano',
    fonte: 'manual',
  }), [getNextAmostraId]);

  // Handler para abrir o modal de adicionar
  const handleOpenAddModal = useCallback(() => {
    const defaultAmostra = getDefaultAmostra();
    addForm.setFieldsValue(defaultAmostra);
    setIsAddModalOpen(true);
  }, [addForm, getDefaultAmostra]);

  // Handler para adicionar nova amostra
  const handleAddAmostra = useCallback(async () => {
    try {
      const values = await addForm.validateFields();

      // Pega imagemUrl explicitamente do form (pode não vir no validateFields se o Form.Item usa componente customizado)
      const imagemUrl = addForm.getFieldValue('imagemUrl');

      // Calcular o próximo ranking baseado nas amostras existentes (excluindo Avaliando)
      const amostrasExistentes = amostrasLocais.filter(a => a.identificacao !== 'Avaliando');
      const proximoRanking = amostrasExistentes.length + 1;

      const novaAmostra: AmostraPadrao = {
        ...getDefaultAmostra(),
        ...values,
        imagemUrl, // Garante que imagemUrl seja incluído
        identificacao: getNextAmostraId(),
        fonte: values.fonte || 'manual',
        selecionadaHomogeneizacao: true, // Por padrão, selecionada para o cálculo
        rankingHomogeneizacao: proximoRanking, // Ranking automático
      };

      // Adicionar ao estado local
      setAmostrasLocais((prev) => [...prev, novaAmostra]);
      setIsAddModalOpen(false);
      addForm.resetFields();
    } catch (error) {
      // Validação falhou
    }
  }, [addForm, getDefaultAmostra, getNextAmostraId, amostrasLocais]);

  // Handler para upload de imagem diretamente no card
  const handleImageUpload = useCallback((identificacao: string, imageUrl: string) => {
    setAmostrasLocais(prev => prev.map(a =>
      a.identificacao === identificacao
        ? { ...a, imagemUrl: imageUrl } as AmostraPadrao
        : a
    ));
  }, []);

  // Handler para deletar amostra
  const handleDeleteAmostra = (identificacao: string) => {
    // 1. Remove do estado local e renumera as restantes (#1..#N), para não
    // deixar "buracos" na numeração (ex.: sobrar #4 e #5 após excluir #1..#3)
    const restantes = amostrasLocais.filter(
      (amostra) => amostra.identificacao !== identificacao
    );
    const { amostras: renumeradas, idMapping } = renumerarAmostrasSequencialmente(restantes);
    setAmostrasLocais(renumeradas);

    // 2. Remove de todas as tabelas e aplica a mesma renumeração
    const tabelasSemAmostra = {
      dadosDepreciacao: dadosDepreciacao.filter(
        (item: DadosDepreciacao) => item.identificacao !== identificacao
      ),
      dadosAreaConstruida: dadosAreaConstruida.filter(
        (item: DadosAreaConstruida) => item.identificacao !== identificacao
      ),
      dadosValorTerreno: dadosValorTerreno.filter(
        (item: DadosValorTerreno) => item.amostra !== identificacao
      ),
      dadosComparativoDireto: dadosComparativoDireto.filter(
        (item: DadosComparativoDireto) => item.amostra !== identificacao
      ),
    };

    // 3. Propaga as mudanças
    onUpdate(renomearIdsNasTabelas(idMapping, tabelasSemAmostra));
  };

  // Handler para atualizar metodologia
  const handleMetodologiaChange = (value: string) => {
    setMetodologia(value);
    onUpdate({ metodologia: value });
  };

  // Handler para atualizar parâmetros de fatoração
  const handleParametrosFatoracaoChange = (values: string[]) => {
    setParametrosFatoracao(values);
    onUpdate({ parametrosFatoracao: values });
  };

  // ============================================
  // REGRAS DE METODOLOGIA POR TIPO DE IMÓVEL
  // ============================================
  const tipoImovel = avaliacaoData.dadosImovel.tipoImovel || '';

  // Determina qual metodologia é permitida/obrigatória por tipo de imóvel
  const getMetodologiaConfig = useMemo(() => {
    // Tipos que DEVEM usar Comparativo Direto (não possuem terreno próprio ou são padronizados)
    const tiposComparativoDireto = [
      'Apartamento',
      'Kitnet',
      'Loft',
      'Cobertura',
      'Flat',
      'Studio',
    ];

    // Tipos que DEVEM usar Evolutivo (construção única com terreno)
    const tiposEvolutivo = [
      'Casa',
    ];

    // Tipos que podem escolher (depende do contexto)
    // tiposAmbos: Sobrado, Industrial, Comercial, Galpão, Loja, Sala Comercial, Terreno

    if (tiposComparativoDireto.includes(tipoImovel)) {
      return {
        helpText: `${tipoImovel} deve usar Comparativo Direto de Mercado (imóvel padronizado/sem terreno próprio)`,
      };
    }

    if (tiposEvolutivo.includes(tipoImovel)) {
      return {
        helpText: `${tipoImovel} deve usar Método Evolutivo (construção única com terreno)`,
      };
    }

    // Para tipos que podem escolher ou tipos não mapeados
    return {
      helpText: undefined,
    };
  }, [tipoImovel]);

  // Automaticamente define a metodologia baseado no tipo de imóvel
  useEffect(() => {
    // Se há metodologia obrigatória, usa ela
    // Se pode escolher e ainda não tem metodologia definida, usa o padrão por tipo
    if (!data.metodologia) {
      const metodologiaPadrao = getMetodologiaPadrao(tipoImovel);
      setMetodologia(metodologiaPadrao);
      onUpdate({ metodologia: metodologiaPadrao });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metodologia, data.metodologia, onUpdate, tipoImovel]);

  // Tipo do imóvel avaliando — necessário para escolher a fórmula correta de valor parcial
  // quando a metodologia é Comparativo Direto de Mercado.
  const tipoImovelAvaliando = useMemo(() => {
    const avaliando = amostrasLocais.find((a) => a.identificacao === 'Avaliando');
    return (
      avaliando?.tipoImovel ||
      avaliacaoData?.dadosImovel?.tipoImovel ||
      identificacaoData?.tipoImovel ||
      ''
    );
  }, [amostrasLocais, avaliacaoData?.dadosImovel?.tipoImovel, identificacaoData?.tipoImovel]);

  const isAvaliandoTerreno = tipoImovelAvaliando === 'Terreno';
  const isComparativoDiretoMetodologia = metodologia === 'Comparativo Direto de Mercado';

  // Hooks para cálculo de valores - usados nos componentes individuais
  // Regras:
  //  - Evolutivo: terreno = areaTerreno × VU terreno (saneado); construida = areaConstruida × CUB ajustado
  //  - Comparativo Direto + apto/casa padronizada: terreno = 0; construida = areaConstruida × VU homog. CD
  //  - Comparativo Direto + Terreno (avaliando): terreno = areaTerreno × VU homog. CD; construida = 0
  const valorParcialTerreno = useValorParcialTerreno(
    isComparativoDiretoMetodologia
      ? (isAvaliandoTerreno ? areaImovelAvaliando : 0)
      : areaImovelAvaliando,
    isComparativoDiretoMetodologia
      ? (isAvaliandoTerreno ? valorHomogeneizadoSaneadoComparativoDireto : 0)
      : valorUnitarioSaneado
  );

  const valorParcialConstruida = useValorParcialConstruida(
    isComparativoDiretoMetodologia && isAvaliandoTerreno ? 0 : areaConstruidaAvaliando,
    isComparativoDiretoMetodologia
      ? valorHomogeneizadoSaneadoComparativoDireto
      : valorUnitarioConstrucao
  );

  const { valorUnitarioNaoAverbada, valorParcialNaoAverbada } = useValorNaoAverbada(
    data.especificarAreaNaoAverbada || false,
    metodologia === 'Comparativo Direto de Mercado'
      ? valorHomogeneizadoSaneadoComparativoDireto
      : valorUnitarioConstrucao,
    data.fatorAjusteNaoAverbada || 1,
    data.areaNaoAverbada || 0
  );

  const { valorTotalSomenteAverbada, valorTotalComNaoAverbada } = useValorTotal(
    valorParcialTerreno,
    valorParcialConstruida,
    valorParcialNaoAverbada,
    data.especificarAreaNaoAverbada || false
  );

  // Estado para controlar se a prestadora optou por preencher os cálculos
  const [showCalculationsForm, setShowCalculationsForm] = useState(false);

  // Tela de decisão: preencher cálculos ou pular (modo create/edit - prestadora)
  const showSkipScreen = onSkipCalculations && mode !== 'calculations' && mode !== 'review' && !showCalculationsForm;

  if (showSkipScreen) {
    return (
      <div className="amostras-step">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 350,
          padding: '40px 20px',
          textAlign: 'center',
        }}>
          <CalculatorOutlined style={{ fontSize: 48, color: '#8c8c8c', marginBottom: 16 }} />
          <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px', color: '#262626' }}>
            Cálculos e Metodologia
          </h3>
          <p style={{ fontSize: 14, color: '#595959', maxWidth: 480, margin: '0 0 32px', lineHeight: 1.6 }}>
            Você pode preencher os cálculos agora ou pular esta etapa.
            Ao pular, o banco poderá realizar os cálculos ou encaminhar para outra prestadora.
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              type="primary"
              onClick={() => setShowCalculationsForm(true)}
              size="large"
            >
              Preencher Cálculos
            </Button>
            <Button
              type="default"
              onClick={onSkipCalculations}
              size="large"
            >
              Pular Cálculos
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="amostras-step">
      {/* Seleção de Metodologia */}
      <Card>
        <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
          Metodologia de Avaliação
        </h4>

        <Form layout="vertical">
          <Form.Item
            label="Selecione a metodologia"
          >
            <ReviewFieldWrapper
              fieldPath="amostras.metodologia"
              fieldLabel="Metodologia de Avaliação"
              stepName="Metodologia"
              allowEditAllInCorrection={hasMetodologiaCorrection}
            >
              <Select
                value={metodologia}
                onChange={handleMetodologiaChange}
                style={{ maxWidth: 400 }}
                disabled={isReviewMode || (isCorrectionMode && !hasMetodologiaCorrection)}
                options={[
                  {
                    label: 'Comparativo Direto de Mercado',
                    value: 'Comparativo Direto de Mercado',
                    disabled: false,
                  },
                  {
                    label: 'Método Evolutivo',
                    value: 'Método Evolutivo',
                    disabled: false,
                  },
                ]}
              />
            </ReviewFieldWrapper>
          </Form.Item>
        </Form>
      </Card>

      {/* Seção de Amostras de Mercado */}
      <Card style={{ marginTop: 24 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <div>
            <ReviewFieldWrapper
              fieldPath="amostras.amostrasMercado"
              fieldLabel="Amostras de Mercado"
              stepName="Metodologia"
              linkInline={true}
            >
              <h4 style={{ fontSize: 16, fontWeight: 600 }}>Amostras de Mercado</h4>
            </ReviewFieldWrapper>
            <p style={{ color: '#666', fontSize: 13, margin: 0 }}>
              Adicione imóveis comparativos para a avaliação (mínimo 5 amostras)
            </p>
          </div>

          {/* Botão só aparece se não está em review mode E (não está em correção OU há correção na seção amostras) */}
          {!isReviewMode && (!isCorrectionMode || hasSamplesCorrection) && (
            <Button icon={<PlusOutlined />} buttonColor="primary" onClick={handleOpenAddModal}>
              Adicionar Amostra Manualmente
            </Button>
          )}
        </div>

        {/* Componente de Scraping inline com toggle Mapa/Tabela */}
        {!isReviewMode && (!isCorrectionMode || hasSamplesCorrection) && (
          <AmostrasScraping
            inspection={inspection}
            highlightedPropertyId={highlightedPropertyId}
            onPropertiesSelected={async (properties: PropertyData[]) => {
              // Contar quantas amostras já existem (excluindo Avaliando)
              const amostrasExistentes = amostrasLocais.filter(a => a.identificacao !== 'Avaliando');
              const qtdExistente = amostrasExistentes.length;

              // Converter PropertyData para AmostraPadrao
              const novasAmostras: AmostraPadrao[] = await Promise.all(properties.map(async (property, index) => {
                // Gerar próximo identificador (#6, #7, etc.)
                const maxId = amostrasLocais.reduce((max, a) => {
                  const match = a.identificacao ? a.identificacao.match(/#(\d+)/) : null;
                  return match && match[1] ? Math.max(max, parseInt(match[1])) : max;
                }, 0);
                const novoId = `#${maxId + index + 1}`;

                // Determinar tipo de imóvel baseado no título
                const tituloLower = property.titulo?.toLowerCase() || '';
                let tipoImovel: 'Casa' | 'Apartamento' | 'Terreno' = 'Casa'; // Default é Casa

                if (tituloLower.includes('apartamento') || tituloLower.includes('apto') || tituloLower.includes('flat') || tituloLower.includes('cobertura') || tituloLower.includes('kitnet') || tituloLower.includes('studio') || tituloLower.includes('loft')) {
                  tipoImovel = 'Apartamento';
                } else if (tituloLower.includes('terreno') || tituloLower.includes('lote') || tituloLower.includes('área') || tituloLower.includes('chácara') || tituloLower.includes('fazenda') || tituloLower.includes('sítio')) {
                  tipoImovel = 'Terreno';
                }
                // Casa permanece como default para: casa, sobrado, ou qualquer outro título

                // Calcular ranking automático baseado na posição
                const rankingPosition = qtdExistente + index + 1;

                // Imagem será carregada via proxy do backend usando externalId
                const propertyExternalId = property.id_unico || undefined;

                return {
                  identificacao: novoId,
                  tipoImovel,
                  areaPrivativa: property.metragem_num || 0,
                  areaTerreno: property.terreno || 0, // Usa valor editado na tabela de scraping
                  valorTotal: property.valor_venda_num || 0,
                  idadeAparente: property.idade ?? 10, // Usa valor editado na tabela ou 10 como padrão
                  estadoConservacao: 'Bom',
                  // Quantidade de cômodos/vagas do scraping
                  quantidadeQuartos: property.quartos_num || 0,
                  quantidadeBanheiros: property.banheiros_num || 0,
                  quantidadeVagas: property.vagas_num || 0,
                  cubBase: 2500, // Valor padrão
                  ajusteAcabamento: 1.0,
                  padrao: 'Médio',
                  topografia: tipoImovel === 'Apartamento' ? 'N/A' : 'Terreno Plano',
                  // Endereço do scraping
                  endereco: property.endereco_completo || '',
                  fonte: property.fonte || 'manual',
                  urlOriginal: property.url,
                  imagemUrl: property.imagem_url || undefined,
                  externalId: propertyExternalId,
                  selecionadaHomogeneizacao: true, // Por padrão, todas as amostras são selecionadas
                  rankingHomogeneizacao: rankingPosition, // Ranking automático baseado na ordem de adição
                } as AmostraPadrao;
              }));

              // Adicionar novas amostras às existentes
              setAmostrasLocais([...amostrasLocais, ...novasAmostras]);
            }}
            disabled={isReviewMode || (isCorrectionMode && !hasSamplesCorrection)}
            aiAnalysisAvailable={aiIsCompleted || aiIsFailed}
            aiAnalysisLoading={aiIsProcessing}
            onViewAiReport={() => setIsAiReportModalOpen(true)}
          />
        )}

        {/* Barra de Instrução para Ranking */}
        {amostrasLocais.filter(a => a.identificacao !== 'Avaliando').length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            marginTop: 24,
            backgroundColor: '#fafafa',
            borderRadius: 8,
            border: '1px solid #e8e8e8'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DragOutlined style={{ fontSize: 16, color: '#8c8c8c' }} />
              <span style={{ color: '#595959', fontSize: 13 }}>
                Arraste para reordenar • Marque as amostras para o cálculo
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                {amostrasLocais.filter(a => a.identificacao !== 'Avaliando' && a.selecionadaHomogeneizacao).length} de {amostrasLocais.filter(a => a.identificacao !== 'Avaliando').length}
              </span>
              <Button
                size="small"
                onClick={() => {
                  setAmostrasLocais(prev => prev.map(a =>
                    a.identificacao === 'Avaliando' ? a : { ...a, selecionadaHomogeneizacao: true } as AmostraPadrao
                  ));
                }}
                disabled={isReviewMode || (isCorrectionMode && !hasSamplesCorrection)}
              >
                Selecionar todas
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setAmostrasLocais(prev => prev.map(a =>
                    a.identificacao === 'Avaliando' ? a : { ...a, selecionadaHomogeneizacao: false } as AmostraPadrao
                  ));
                }}
                disabled={isReviewMode || (isCorrectionMode && !hasSamplesCorrection)}
              >
                Desmarcar todas
              </Button>
            </div>
          </div>
        )}

        {/* Lista de Cards de Amostras com Drag and Drop */}
        <div style={{ marginTop: 12 }}>
          <AmostrasList
            amostras={amostrasLocais}
            onAmostrasReorder={(novasAmostras) => {
              // Preserva o Avaliando e renumera as demais (#1..#N) conforme a nova ordem
              const avaliando = amostrasLocais.find(a => a.identificacao === 'Avaliando');
              const { amostras: amostrasRenomeadas, idMapping } =
                renumerarAmostrasSequencialmente(novasAmostras);

              setAmostrasLocais(avaliando ? [avaliando, ...amostrasRenomeadas] : amostrasRenomeadas);

              // Se houve mudança de IDs, renomeia também nas tabelas de cálculo
              // para manter os fatores associados às amostras corretas
              if (Object.keys(idMapping).length > 0) {
                onUpdate(
                  renomearIdsNasTabelas(idMapping, {
                    dadosDepreciacao,
                    dadosAreaConstruida,
                    dadosValorTerreno,
                    dadosComparativoDireto,
                  })
                );
              }
            }}
            onEdit={handleEditAmostra}
            onDelete={handleDeleteAmostra}
            onSelectionChange={(identificacao, selected) => {
              setAmostrasLocais(prev => prev.map(a =>
                a.identificacao === identificacao
                  ? { ...a, selecionadaHomogeneizacao: selected } as AmostraPadrao
                  : a
              ));
            }}
            onImageUpload={handleImageUpload}
            metodologia={metodologia}
            hideActions={isReviewMode || (isCorrectionMode && !hasSamplesCorrection)}
            disabled={isReviewMode || (isCorrectionMode && !hasSamplesCorrection)}
          />
        </div>

        {/* Card de Adicionar Nova Amostra - oculto em modo de revisão ou se em correção sem marcação de amostras */}
        {!isReviewMode && (!isCorrectionMode || hasSamplesCorrection) && (
          <div
            onClick={handleOpenAddModal}
            style={{
              border: '2px dashed #d9d9d9',
              borderRadius: 8,
              padding: 24,
              marginTop: 12,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              backgroundColor: '#fafafa',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1890ff';
              e.currentTarget.style.color = '#1890ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d9d9d9';
              e.currentTarget.style.color = 'inherit';
            }}
          >
            <PlusOutlined style={{ fontSize: 24, marginBottom: 8 }} />
            <span style={{ fontSize: 14 }}>Clique para adicionar nova amostra</span>
          </div>
        )}
      </Card>

      {metodologia === 'Comparativo Direto de Mercado' ? (
        /* METODOLOGIA: COMPARATIVO DIRETO DE MERCADO */
        <>
          {/* 1. Tabela de Homogeneização */}
          <TabelaComparativoDireto
            dados={dadosComparativoDiretoFiltrado}
            onUpdate={(novosDados) => {
              const merged = dadosComparativoDireto.map((orig) => {
                const atualizado = novosDados.find((n: any) => n.amostra === orig.amostra);
                return atualizado || orig;
              });
              onUpdate({ dadosComparativoDireto: merged });
            }}
            parametrosFatoracao={parametrosFatoracao}
            onParametrosFatoracaoChange={handleParametrosFatoracaoChange}
            disabled={isReviewMode || (isCorrectionMode && !hasComparativoDiretoCorrection)}
            displayIdMap={displayIdMap}
          />

          {/* 2. Cálculos Avaliatórios - Select de Grau de Precisão e Fundamentação */}
          <Card style={{ marginTop: 24 }}>
            <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              Cálculos avaliatórios
            </h4>
            <ClassificacaoSection
              grauPrecisao={data.grauPrecisao || 'Grau de Precisão III'}
              grauFundamentacao={data.grauFundamentacao || 'Grau de Fundamentação II'}
              onUpdateClassificacao={(campo, valor) => {
                onUpdate({ [campo]: valor });
              }}
              disabled={isReviewMode}
            />
          </Card>

          {/* 3. Card de Homogeneização */}
          <Card style={{ marginTop: 24 }}>
            <CardHomogeneizacao
              dadosValorTerreno={dadosComparativoDiretoComparacao.map((item) => ({
                amostra: item.amostra,
                valorUnitarioFinal: item.valorHomogeneizado,
                tipoImovel: 'Apartamento' as const,
                areaTerreno: 0,
                padrao: '',
                topografia: '',
                valorTotalAmostra: 0,
                valorTerreno: 0,
                valorUnitario: 0,
                indiceFiscal: 1,
                fatorOferta: 1,
                fatorTopografia: 1,
                fatorArea: 1,
                fatorTestada: 1,
                fatorVegetacao: 1,
                fatorExtraI: 1,
                fatorExtraII: 1,
                fatorExtraIII: 1,
                fat: 1,
              }))}
              onAlertas={setAlertasHomogeneizacao}
            />
          </Card>

          {/* Área Construída Não Averbada */}
          <Card style={{ marginTop: 24 }}>
            <AreaNaoAverbadaSection
              especificarAreaNaoAverbada={data.especificarAreaNaoAverbada || false}
              areaNaoAverbada={data.areaNaoAverbada || 0}
              fatorAjusteNaoAverbada={data.fatorAjusteNaoAverbada || 1}
              valorUnitarioNaoAverbada={valorUnitarioNaoAverbada}
              valorParcialNaoAverbada={valorParcialNaoAverbada}
              onUpdateAreaNaoAverbada={(campo, valor) => {
                onUpdate({ [campo]: valor });
              }}
              disabled={isReviewMode || (isCorrectionMode && !hasAreaNaoAverbadaCorrection)}
            />
          </Card>

          {/* 7. Quadro de Resumo */}
          <Card style={{ marginTop: 24 }}>
            <ResumoFinalSection
              valorTotalSomenteAverbada={valorTotalSomenteAverbada}
              valorTotalComNaoAverbada={valorTotalComNaoAverbada}
              especificarAreaNaoAverbada={data.especificarAreaNaoAverbada || false}
            />
          </Card>
        </>
      ) : (
        /* METODOLOGIA: MÉTODO EVOLUTIVO (padrão) */
        <>
          {/* 1. Tabela de Depreciação */}
          <CalculoDepreciacao dados={dadosDepreciacaoFiltrado} onUpdate={handleDepreciacaoUpdate} disabled={isReviewMode || (isCorrectionMode && !hasDepreciacaoCorrection)} displayIdMap={displayIdMap} />

          {/* 2. Tabela de Cálculo da Área Construída */}
          <TabelaAreaConstruida
            dados={dadosAreaConstruidaFiltrado}
            valoresTotais={valoresTotaisAmostras}
            onUpdate={(novosDados) => {
              const merged = dadosAreaConstruida.map((orig) => {
                const atualizado = novosDados.find((n: any) => n.identificacao === orig.identificacao);
                return atualizado || orig;
              });
              onUpdate({ dadosAreaConstruida: merged });
            }}
            disabled={isReviewMode || (isCorrectionMode && !hasAreaConstruidaCorrection)}
            displayIdMap={displayIdMap}
          />

          {/* 3. Tabela do Cálculo do Valor do Terreno */}
          <TabelaValorTerreno
            dados={dadosValorTerrenoFiltrado}
            switches={switchesTerreno}
            dadosAreaConstruida={dadosAreaConstruida}
            onUpdateDados={(novosDados) => {
              const merged = dadosValorTerreno.map((orig) => {
                const atualizado = novosDados.find((n: any) => n.amostra === orig.amostra);
                return atualizado || orig;
              });
              onUpdate({ dadosValorTerreno: merged });
            }}
            onUpdateSwitches={(novosSwitches) => {
              onUpdate({ switchesTerreno: novosSwitches });
            }}
            disabled={isReviewMode || (isCorrectionMode && !hasValorTerrenoCorrection)}
            displayIdMap={displayIdMap}
          />

          {/* 4. Card de Homogeneização */}
          <Card style={{ marginTop: 24 }}>
            <CardHomogeneizacao
              dadosValorTerreno={dadosValorTerrenoComparacao}
              onAlertas={setAlertasHomogeneizacao}
            />
          </Card>

          {/* Card de Formação do Valor */}
          <Card style={{ marginTop: 24 }}>
            <CardFormacaoValor
              valorUnitarioSaneado={valorUnitarioSaneado}
              areaInicialTerreno={areaImovelAvaliando}
            />
          </Card>

          {/* 7. Cálculos Avaliatórios - Select de Grau de Precisão e Fundamentação */}
          <Card style={{ marginTop: 24 }}>
            <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              Cálculos avaliatórios
            </h4>
            <ClassificacaoSection
              grauPrecisao={data.grauPrecisao || 'Grau de Precisão III'}
              grauFundamentacao={data.grauFundamentacao || 'Grau de Fundamentação II'}
              onUpdateClassificacao={(campo, valor) => {
                onUpdate({ [campo]: valor });
              }}
              disabled={isReviewMode}
            />
          </Card>

          {/* 8. Seção sobre os Valores do Terreno */}
          <Card style={{ marginTop: 24 }}>
            <TerrenoSection
              areaTerreno={areaImovelAvaliando}
              valorUnitarioTerreno={valorUnitarioSaneado}
              valorParcialTerreno={valorParcialTerreno}
            />
          </Card>

          {/* 9. Seção sobre a Área Construída Averbada */}
          <Card style={{ marginTop: 24 }}>
            <AreaConstruidaSection
              areaConstruida={areaConstruidaAvaliando}
              valorUnitarioConstruida={valorUnitarioConstrucao}
              valorParcialConstruida={valorParcialConstruida}
            />
          </Card>

          {/* 10. Seção sobre a Área Construída Não Averbada */}
          <Card style={{ marginTop: 24 }}>
            <AreaNaoAverbadaSection
              especificarAreaNaoAverbada={data.especificarAreaNaoAverbada || false}
              areaNaoAverbada={data.areaNaoAverbada || 0}
              fatorAjusteNaoAverbada={data.fatorAjusteNaoAverbada || 1}
              valorUnitarioNaoAverbada={valorUnitarioNaoAverbada}
              valorParcialNaoAverbada={valorParcialNaoAverbada}
              onUpdateAreaNaoAverbada={(campo, valor) => {
                onUpdate({ [campo]: valor });
              }}
              disabled={isReviewMode || (isCorrectionMode && !hasAreaNaoAverbadaCorrection)}
            />
          </Card>

          {/* 11. Quadro de Resumo */}
          <Card style={{ marginTop: 24 }}>
            <ResumoFinalSection
              valorTotalSomenteAverbada={valorTotalSomenteAverbada}
              valorTotalComNaoAverbada={valorTotalComNaoAverbada}
              especificarAreaNaoAverbada={data.especificarAreaNaoAverbada || false}
            />
          </Card>
        </>
      )}

      {/* Seção de Comentários e Observações - Para ambas metodologias */}
      <Card style={{ marginTop: 24 }}>
        <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
          Comentários e Observações
        </h4>
        <p style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>
          Adicione observações relevantes sobre a avaliação
        </p>
        <ReviewFieldWrapper
          fieldPath="amostras.comentarios"
          fieldLabel="Comentários e Observações"
          stepName="Metodologia"
        >
          <AntInput.TextArea
            value={comentarios}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              const valor = e.target.value;
              setComentarios(valor);
              onUpdate({ comentarios: valor });
            }}
            placeholder="Digite aqui suas observações sobre a avaliação..."
            rows={4}
            style={{ fontSize: 14 }}
            disabled={isReviewMode}
          />
        </ReviewFieldWrapper>
      </Card>

      {/* Modal de Relatório de Análise IA */}
      <Modal
        title={
          <span>
            <RobotOutlined style={{ marginRight: 8, color: '#722ed1' }} />
            Relatório de Análise com Inteligência Artificial
          </span>
        }
        open={isAiReportModalOpen}
        onCancel={() => setIsAiReportModalOpen(false)}
        footer={null}
        width={1000}
        style={{ top: 20 }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      >
        {aiAnalysisResult ? (
          <RelatorioAnaliseIA
            analysisResult={aiAnalysisResult}
            loading={aiIsProcessing}
            onPropertyClick={handlePropertyClickFromAiReport}
            onImportSamples={handleImportAiSamples}
          />
        ) : aiIsProcessing ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <LoadingOutlined style={{ fontSize: 40, color: '#722ed1' }} spin />
            <p style={{ marginTop: 16, color: '#666' }}>Processando análise de IA...</p>
            <p style={{ color: '#999', fontSize: 12 }}>Isso pode levar alguns minutos</p>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <ExclamationCircleOutlined style={{ fontSize: 40, color: '#faad14' }} />
            <p style={{ marginTop: 16, color: '#666' }}>Nenhum resultado de análise disponível</p>
            <p style={{ color: '#999', fontSize: 12 }}>A análise de IA ainda não foi concluída ou não há dados para exibir</p>
          </div>
        )}
      </Modal>

      {/* Modal de Adicionar Amostra Manualmente */}
      <AmostraModal
        amostra={getDefaultAmostra()}
        isOpen={isAddModalOpen}
        form={addForm}
        onSave={handleAddAmostra}
        onCancel={() => {
          setIsAddModalOpen(false);
          addForm.resetFields();
        }}
        metodologia={metodologia}
        mode="add"
      />
    </div>
  );
}
